import type { KeycloakInstance, KeycloakTokenParsed } from 'keycloak-js';
import { defaultTo } from 'lodash-es';
import { setupAutomaticTokenRefresh } from './automatic-token-refresh';
import type { TokenStorage } from './token-storage';
import { createTokenStorageForInstance, updateTokenStorage } from './token-storage';

export type TokenPayload = KeycloakTokenParsed;

export type AccessTokenPayload = TokenPayload;

export type IdTokenPayload = TokenPayload & {
  email: string;
  name: string;
  given_name: string;
  family_name: string;
  organization: string;
  iss: string;
  preferred_username: string;
};

export type RefreshTokenPayload = TokenPayload;

export class AuthenticationTokens {

  private tokenStorage: TokenStorage;

  constructor(
    private keycloakInstance: KeycloakInstance,
  ) {
    this.tokenStorage = createTokenStorageForInstance(this.keycloakInstance);

    this.startAutomaticTokenRefresh();
  }

  get accessToken(): string | null {
    return defaultTo(this.keycloakInstance.token, null);
  }

  get idToken(): string | null {
    return defaultTo(this.keycloakInstance.idToken, null);
  }

  get refreshToken(): string | null {
    return defaultTo(this.keycloakInstance.refreshToken, null);
  }

  get accessTokenPayload(): AccessTokenPayload | null {
    return defaultTo(this.keycloakInstance.tokenParsed as AccessTokenPayload, null);
  }

  get idTokenPayload(): IdTokenPayload | null {
    return defaultTo(this.keycloakInstance.idTokenParsed as IdTokenPayload, null);
  }

  get refreshTokenPayload(): RefreshTokenPayload | null {
    return defaultTo(this.keycloakInstance.refreshTokenParsed as RefreshTokenPayload, null);
  }

  async refresh() {
    await this.keycloakInstance.updateToken(-1);
    await updateTokenStorage(this.tokenStorage, this.keycloakInstance);
  }

  async destroy() {
    await this.tokenStorage.remove();
    await this.stopAutomaticTokenRefresh();
  }

  private async startAutomaticTokenRefresh() {
    this.stopAutomaticTokenRefresh = await setupAutomaticTokenRefresh(
      () => this.getTokens(),
      () => this.refresh(),
    );
  }

  private stopAutomaticTokenRefresh = () => Promise.resolve();

  private getTokens() {
    return [
      this.accessTokenPayload,
      this.idTokenPayload,
      this.refreshTokenPayload,
    ];
  }
}
